/**
 * INVITE PLAYERS — built to the owner's own mockup (25.9.2026, materials/invite-friends-hq.jpg, 3764 × 6688, which is
 * exactly four source pixels per stage unit) from the game's existing pieces. The owner's own INVITE PLAYERS logo is
 * used unchanged.
 *
 * The markup is NESTED AND FLOWING, the owner's order of 26.9.2026: the popup's frame has a place on the stage and
 * everything inside it is one column that lays itself out. No part of the content carries left/top/width/height —
 * a line that wraps grows its own box and pushes what follows down, and the friends list takes whatever height is
 * left, so it shortens by itself to stay inside the container. Every measurement lives in the stylesheet as padding,
 * gap and margin, where flow can use it.
 *
 * What the popup is for: filling the table before the join window closes. The host has just paid for the match, so an
 * empty seat is a lost game. Four routes to the same end — the room code, the link, the QR, and a direct invite to a
 * friend. The direct invite is the only one the server restricts: it works between friends, only while the room is in
 * LOBBY or MATCH_END, and the same friend cannot be invited to the same room twice within ten minutes (apps/realtime/
 * src/social.ts), which is why a sent invite has to show on its row.
 */
import { art } from '../art';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import qrcode from 'qrcode-generator';
import { social, type SocialOverview } from '../net/api';
import { pub, principal, sheet, showToast, serverNow, frozenFriends } from '../state/store';
import { t } from '../i18n/en';
import { Avatar } from './bits';
import { sfx } from '../audio/sfx';
import { ScrollRail } from './ScrollRail';

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export function InviteSheet({ code }: { code: string }) {
  const link = `${location.origin}/r/${code}`; const linkText = link.replace(/^https?:\/\//, '');
  // GIF, not SVG (SVG is banned in this game's UI); margin is pixels — 32 = the 4-module quiet zone at cellSize 8
  const qrSrc = useMemo(() => { const q = qrcode(0, 'M'); q.addData(link); q.make(); return q.createDataURL(8, 32); }, [link]);
  const [friends, setFriends] = useState<SocialOverview['friends'] | null>(null);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [qr, setQr] = useState(false); const [find, setFind] = useState(''); const [, tick] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (frozenFriends.value) { setFriends(frozenFriends.value); return; } social.overview().then((o) => setFriends(o.friends)).catch(() => setFriends([])); }, []);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 500); return () => clearInterval(id); }, []);
  const close = () => { sheet.value = null; };
  const s = pub.value; const seats = s ? Math.max(0, (s.settings.maxPlayers ?? 6) - s.players.length) : 0;
  const atTable = new Set((s?.players ?? []).map((p) => p.id));
  const deadline = s?.lifecycle.joinDeadline ?? null;
  const left = deadline ? Math.max(0, Math.ceil((deadline - serverNow()) / 1000)) : null;
  const clock = left === null ? '--:--' : `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
  const host = (s?.players ?? []).find((p) => p.id === s?.hostId);
  const iAmHost = !!s && principal.value?.id === s.hostId;
  /* Friends: the proposer pays for the match and the crew comes free. Meet and Flirt: one credit each (OD-005). */
  const houseTable = s?.settings.roomType === 'meet' || s?.settings.roomType === 'flirt';
  const creditLine = houseTable
    ? 'Everyone pays 1 credit for the match'
    : `${iAmHost ? 'You cover' : `${clip(host?.displayName ?? 'The host', 8)} covers`} the 2 credits, ${iAmHost ? 'crew plays' : 'you play'} free`;

  const share = async () => { sfx.tap(); try { if (navigator.share) await navigator.share({ title: 'SayNoWay', text: t.hook, url: link }); else { await navigator.clipboard.writeText(link); showToast(t.copied); } } catch { /* cancelled */ } };
  const copy = async () => { sfx.tap(); try { await navigator.clipboard.writeText(link); showToast(t.copied); } catch { showToast(linkText); } };
  const invite = async (id: string) => { sfx.tap(); try { await social.invite(id, code); setSent((x) => new Set(x).add(id)); showToast('Invite sent'); } catch (e) { showToast((e as { code?: string }).code === 'EXISTS' ? 'Already invited' : 'Could not invite'); } };

  const all = friends ?? [];
  const q = find.trim().toLowerCase();
  const list = (q ? all.filter((f) => f.displayName.toLowerCase().includes(q)) : all)
    .slice().sort((a, b) => Number(atTable.has(a.id)) - Number(atTable.has(b.id)));
  const searchOn = all.length > 8;
  const ways: [string, string, () => void, string][] = [
    ['COPY LINK', '/art/lobby-kit/icon.link.png', () => void copy(), 'btn-copy-link'],
    ['SHARE', '/art/ui4b/icon-share@2x.png', () => void share(), 'btn-share'],
    [qr ? 'HIDE QR' : 'SHOW QR', '/art/join-cut/icon.scan.png', () => { sfx.tap(); setQr((v) => !v); }, 'btn-qr'],
  ];

  return (
    <div class="sheet-backdrop" onClick={close} data-testid="invite-sheet">
      <div class="screen abs invite51" role="dialog" aria-label="Invite players" onClick={(e) => e.stopPropagation()}>
        <span class="iv-frame" />
        {(['tl', 'tr', 'bl', 'br'] as const).map((c) => <img key={c} class={`iv-palm iv-palm-${c}`} src={art(`/art/howto-cut/slice/palm-${c}.png`)} alt="" draggable={false} />)}
        <img class="iv-title" src={art('/art/popup/invite-players.title.png')} role="img" aria-label="INVITE PLAYERS" alt="INVITE PLAYERS" draggable={false} />
        <button class="iv-close" type="button" data-testid="invite-close" aria-label="Close">
          <img src={art('/art/ui4b/btn-close-x@2x.png')} alt="" draggable={false} onClick={close} />
        </button>

        {/* the column: everything below finds its own place */}
        <div class="iv-body">
          <div class="iv-sub">SAME CREW. ANOTHER ROUND?</div>

          {/* seats and the join window: the reason to hurry */}
          <div class="iv-status">
            <img class="iv-ic iv-people" src={art('/art/lobby-kit/icon.people.png')} alt="" draggable={false} />
            <span class="iv-num" data-testid="invite-spots">{seats}</span>
            <span class="iv-lbl">SPOTS LEFT</span>
            <img class="iv-vline" src={art('/art/lobby-kit/row.divider.png')} alt="" draggable={false} />
            <img class="iv-ic iv-clock" src={art('/art/lobby-kit/icon.clock.png')} alt="" draggable={false} />
            <span class="iv-num" data-testid="invite-clock">{clock}</span>
            <span class="iv-lbl light">LEFT TO JOIN</span>
          </div>

          {/* the code, and the link inside the same box */}
          <div class="iv-room">
            <div class="iv-room-lbl">ROOM CODE</div>
            <div class="iv-code" data-testid="invite-code">{code}</div>
            <button class="iv-link" type="button" aria-label="Copy the link" onClick={() => void copy()}>
              <img class="iv-lic" src={art('/art/lobby-kit/icon.link.png')} alt="" draggable={false} />
              <span class="iv-url">{linkText}</span>
              <img class="iv-cpy" src={art('/art/lobby-kit/icon.copy.png')} alt="" draggable={false} />
            </button>
          </div>

          {/* three ways out of the room: copy, the phone's own share, the QR */}
          <div class="iv-ways">
            {ways.map(([label, icon, run, testid], i) => (
              <button key={testid} class={`iv-pill iv-p${i + 1}${i === 2 && qr ? ' on' : ''}`} type="button"
                style={{ '--plate': `url(${art('/art/popup/slice/pill.large.v2.png')})` }}
                onClick={run} data-testid={testid} aria-label={label}>
                <img class={i === 2 ? 'iv-scan' : undefined} src={art(icon)} alt="" draggable={false} />
                <span>{label}</span>
              </button>))}
          </div>

          {/* what the match costs whoever comes: the diamond and the line, centred, the line free to wrap */}
          <div class="iv-credits">
            <img class="iv-gem" src={art('/art/home/hud-gem@2x.png')} alt="" draggable={false} />
            <span class="iv-c1">{creditLine}</span>
          </div>

          <div class="iv-fhead">
            <span class="iv-friends">YOUR FRIENDS</span>
            <span class="iv-count">{all.length || ''}</span>
          </div>

          {searchOn && (
            <div class="iv-search">
              <input class="iv-find" value={find} placeholder="Search friends..." data-testid="invite-search" aria-label="Search friends"
                onInput={(e) => setFind((e.target as HTMLInputElement).value)} />
            </div>)}

          {qr
            ? <div class="iv-qrwrap"><img class="iv-qr" src={qrSrc} alt="Room QR code" draggable={false} data-testid="invite-qr" /></div>
            : <div class="iv-listwrap">
              <div class="iv-list" ref={listRef} data-testid="invite-list">
                {friends === null && <div class="iv-empty">Loading…</div>}
                {friends !== null && all.length === 0 && <div class="iv-empty">No friends yet. Share the link — you win friends by playing.</div>}
                {friends !== null && all.length > 0 && list.length === 0 && <div class="iv-empty">No name matches “{find}”.</div>}
                {list.map((f) => { const here = atTable.has(f.id); const done = sent.has(f.id);
                  const state = here ? 'here' : done ? 'sent' : 'open';
                  return (
                    <div key={f.id} class="iv-row" data-testid={`iv-${f.id}`}>
                      <span class="iv-face">
                        <Avatar avatarId={f.avatarId} name={f.displayName} state="happy" bare cls="disc" />
                        <img class="iv-ring" src={art('/art/game-kit/ring-gold-80@2x.png')} alt="" draggable={false} />
                      </span>
                      <img class={`iv-dot ${here ? 'on' : 'off'}`} src={art('/art/home/hud-bellbadge@2x.png')} alt="" draggable={false} />
                      <span class="iv-name">{clip(f.displayName, 12)}</span>
                      <button class={`iv-act ${state}`} type="button" disabled={state !== 'open'}
                        data-testid={`iv-invite-${f.id}`} aria-label={`Invite ${f.displayName}`}
                        onClick={state === 'open' ? () => void invite(f.id) : undefined}>{here ? 'AT TABLE' : done ? 'SENT' : 'INVITE'}</button>
                    </div>); })}
              </div>
              {/* the game's own scrollbar, the same drawn piece YOUR PROFILE wears */}
              <ScrollRail target={listRef} testid="invite-scrollbar" />
            </div>}
        </div>
      </div>
    </div>
  );
}
